import type {
  AssetPosition,
  AssetPositionAction,
  CompanyAssetAction,
  ConfigAssetAction,
  FirmwareUpgradeAssetAction,
  RelayAssetAction,
} from "kubb";
import { actionTypesEnum } from "kubb";
import type React from "react";
import { useState } from "react";
import { isEdgeGateway } from "utils/gateway";
import type { Asset } from "utils/object";
import { errorToTitleAndText } from "../../../components/Error/errorMessages";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import {
  useCreateAssetAction,
  useCreateAssetPositionAction,
} from "../../../hooks/createEntity";
import FirmwareUpgradeSubForm, {
  FirmwareUpgradeInitialValues,
  FirmwareUpgradeSubFormSchema,
} from "./FirmwareUpgradeForm";
import LansenFirmwareUpgradeSubForm, {
  LansenFirmwareUpgradeInitialValues,
  LansenFirmwareUpgradeSubFormSchema,
} from "./LansenFirmwareUpgradeForm";

interface Props {
  close: () => void;
  show: boolean;
  description: string;
  onComplete?: () => void;
  onClose?: () => void;
}

type PProps = Props &
  (
    | {
        asset: Asset;
        action:
          | CompanyAssetAction
          | RelayAssetAction
          | ConfigAssetAction
          | FirmwareUpgradeAssetAction;
        assetPosition?: never;
        assetPositionAction?: never;
      }
    | {
        asset?: never;
        action?: never;
        assetPosition: AssetPosition;
        assetPositionAction: AssetPositionAction;
      }
  );

interface FormValues {
  format_flag?: boolean;
  debug_flag?: boolean;
  target_version?: number;
}

const AssetActionConfirmModal = (props: PProps): React.ReactElement => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);

  const { update, error } = props.asset
    ? useCreateAssetAction(props.asset)
    : useCreateAssetPositionAction(props.assetPosition);

  const isFirmwareUpgrade =
    props.asset &&
    props.action?.action_type === actionTypesEnum.FIRMWARE_UPGRADE;

  const isLansenFirmwareUpgrade =
    isFirmwareUpgrade &&
    "manufacturer" in props.asset &&
    props.asset.manufacturer === "lansen";

  const isEdgeFirmwareUpgrade =
    isFirmwareUpgrade &&
    "manufacturer" in props.asset &&
    "model" in props.asset &&
    isEdgeGateway(props.asset);

  const handleSubmit = (values?: FormValues): void => {
    setStarted(true);

    const promise = props.asset
      ? (
          update as (
            d:
              | CompanyAssetAction
              | RelayAssetAction
              | ConfigAssetAction
              | FirmwareUpgradeAssetAction,
          ) => Promise<
            [
              (
                | CompanyAssetAction
                | RelayAssetAction
                | ConfigAssetAction
                | FirmwareUpgradeAssetAction
              ),
              any,
            ]
          >
        )({
          ...props.action,
          ...values,
        })
      : (
          update as (
            d: AssetPositionAction,
          ) => Promise<[AssetPositionAction, any]>
        )(props.assetPositionAction);

    promise
      .then(() => {
        setCompleted(true);
        if (props.onComplete) {
          props.onComplete();
        }
      })
      .catch(() => {
        setCompleted(true);
      });
  };

  const handleClose = (): void => {
    if (props.onClose) {
      props.onClose();
    }
    props.close();
    setTimeout(() => {
      setStarted(false);
      setCompleted(false);
    }, 200);
  };

  const getSubForm = () => {
    if (isFirmwareUpgrade && !completed) {
      if (isEdgeFirmwareUpgrade) {
        // Edge devices use LWM2M FOTA — no form options needed
        return null;
      }
      if (isLansenFirmwareUpgrade) {
        return <LansenFirmwareUpgradeSubForm namespace="" disabled={started} />;
      }
      return <FirmwareUpgradeSubForm namespace="" disabled={started} />;
    }
    return null;
  };

  const getFormProps = () => {
    if (isFirmwareUpgrade) {
      if (isEdgeFirmwareUpgrade) {
        // Edge devices use LWM2M FOTA — no form, just confirm
        return {};
      }
      if (isLansenFirmwareUpgrade) {
        return {
          initialValues: LansenFirmwareUpgradeInitialValues,
          validationSchema: LansenFirmwareUpgradeSubFormSchema,
          onSubmit: (values: FormValues) => handleSubmit(values),
        };
      }
      return {
        initialValues: FirmwareUpgradeInitialValues,
        validationSchema: FirmwareUpgradeSubFormSchema,
        onSubmit: (values: FormValues) => handleSubmit(values),
      };
    }
    return {};
  };

  return (
    <ConfirmModal
      buttonLabel={started ? "Starting..." : "OK"}
      title={
        completed
          ? error
            ? `Error${
                errorToTitleAndText(error).title
                  ? `: ${errorToTitleAndText(error).title}`
                  : ""
              }`
            : "Action Started"
          : "Are you sure?"
      }
      body={
        completed ? (
          error ? (
            <p>
              There was an error starting your action
              {errorToTitleAndText(error).text}
            </p>
          ) : (
            <p>Your action has been started</p>
          )
        ) : (
          <>
            <p>Do you really want to {props.description}?</p>
            {getSubForm()}
          </>
        )
      }
      show={props.show}
      handleAction={() => handleSubmit()}
      disabled={started || completed}
      closeLabel={completed ? "Close" : "Cancel"}
      handleClose={handleClose}
      {...getFormProps()}
    />
  );
};

export default AssetActionConfirmModal;
